/**
 * DemoDirectoryPage (`/demos`)
 *
 * Two-section public directory:
 *   1. Open Demo  — the Nexengines platform demo at `/demo`, no code
 *      required. Anyone can click through.
 *   2. Client Demos — bespoke per-business demos managed by admin via
 *      `/admin/demos`. Each one is gated by a 5-digit code the operator
 *      reads out loud at the unlock screen.
 *
 * The public API strips codes before returning the list, so nothing
 * sensitive renders on this page.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import { Building2, KeyRound, ArrowRight, PlayCircle, Cpu } from "lucide-react";
import DemoTopBar from "@/components/DemoTopBar";

export default function DemoDirectoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/demo-directory`);
        if (alive) setItems(data.items || []);
      } catch {
        if (alive) setError("Could not load directory. Try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section
      data-testid="demo-directory-page"
      className="min-h-screen bg-[#0A192F] text-slate-100"
    >
      <DemoTopBar />
      <div className="py-16 px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-brand-cyan font-mono text-[11px] tracking-[0.24em] uppercase mb-3">
          <KeyRound className="w-3.5 h-3.5" /> // LIVE DEMO DIRECTORY
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
          Explore a demo.<br />
          <span className="text-brand-cyan">Public or client-specific.</span>
        </h1>
        <p className="text-slate-400 mt-4 max-w-2xl text-base md:text-lg">
          The open demo below runs the full Nexengines platform — no code needed.
          Client demos are bespoke engines unlocked with a 5-digit code your operator reads to you.
        </p>

        {/* ── Section 1: Open Demo (no code) ───────────────────────── */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-emerald">
              // OPEN DEMO · NO CODE REQUIRED
            </div>
          </div>
          <Link
            to="/demo"
            data-testid="demo-open-tile"
            className="group flex items-center justify-between gap-4 rounded-xl border border-emerald-500/30 hover:border-brand-emerald/70 bg-navy-900/60 hover:bg-navy-900/80 backdrop-blur-md px-5 py-5 transition"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center shrink-0">
                <Cpu className="w-5 h-5 text-navy-950" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">Nexengines Platform Demo</div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-400 mt-1 flex items-center gap-1.5">
                  <PlayCircle className="w-3 h-3" /> Open · click to launch
                </div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-brand-emerald transition" />
          </Link>
        </div>

        {/* ── Section 2: Client Demos (code required) ──────────────── */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-brand-cyan">
              // CLIENT DEMOS · 5-DIGIT CODE REQUIRED
            </div>
          </div>

          {loading && (
            <div data-testid="demo-directory-loading" className="text-slate-500 font-mono text-sm">
              Loading directory…
            </div>
          )}
          {error && !loading && (
            <div data-testid="demo-directory-error" className="text-rose-400 font-mono text-sm">
              {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div
              data-testid="demo-directory-empty"
              className="rounded-xl border border-white/10 bg-navy-900/60 backdrop-blur-md p-8 text-center text-slate-400"
            >
              No client demos published yet. Check back soon.
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <ul
              data-testid="demo-directory-list"
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {items.map((it) => (
                <li key={it.slug}>
                  <Link
                    to={`/demos/${it.slug}/unlock`}
                    data-testid={`demo-tile-${it.slug}`}
                    className="group flex items-center justify-between gap-4 rounded-xl border border-white/10 hover:border-brand-cyan/60 bg-navy-900/60 hover:bg-navy-900/80 backdrop-blur-md px-5 py-5 transition"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {!it.logo_url && (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center shrink-0">
                          <Building2 className="w-5 h-5 text-navy-950" />
                        </div>
                      )}
                      <div className="min-w-0">
                        {it.logo_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${it.logo_url}`}
                            alt={it.business_name}
                            data-testid={`demo-tile-logo-${it.slug}`}
                            className="h-9 w-auto max-w-[220px] object-contain"
                          />
                        ) : (
                          <div className="font-semibold text-white truncate">{it.business_name}</div>
                        )}
                        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-1 flex items-center gap-1.5">
                          <KeyRound className="w-3 h-3" /> Requires 5-digit code
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-brand-cyan transition" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      </div>
    </section>
  );
}
